import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" • ");
  return typeof d === "object" ? JSON.stringify(d) : String(d);
}

async function _triggerBlobDownload(blob, filename, contentType) {
  // Force text/csv (or whatever the server sent) MIME so the browser treats
  // the blob as a proper file, not an octet-stream that some browsers refuse
  // to save. Also, delay revokeObjectURL — synchronously revoking right after
  // `a.click()` cancels the download in Safari/Firefox and sometimes Chrome.
  const typed = contentType && contentType !== blob.type
    ? new Blob([await blob.arrayBuffer()], { type: contentType })
    : blob;
  const url = URL.createObjectURL(typed);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Give the browser a beat to start the download before releasing the URL.
  setTimeout(() => {
    try { a.remove(); } catch (_) { /* ignore */ }
    try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
  }, 1500);
}

export async function downloadFile(pathWithApi, filename) {
  const token = localStorage.getItem("access_token");
  const res = await fetch(`${API}${pathWithApi}`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: "include",
  });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try {
      const t = await res.text();
      if (t) msg = t.length > 300 ? t.slice(0, 300) : t;
    } catch (_) { /* ignore */ }
    throw new Error(msg);
  }
  const contentType = res.headers.get("content-type") || "application/octet-stream";
  // Honor server-supplied filename from Content-Disposition when present —
  // critical for endpoints that dynamically switch between .csv/.txt/.xlsx/.zip
  // (e.g., Amazon Shipping export returns a zip when no account_id set).
  const disposition = res.headers.get("content-disposition") || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const finalName = match ? decodeURIComponent(match[1].trim()) : filename;
  const blob = await res.blob();
  if (!blob || blob.size === 0) {
    throw new Error("Downloaded file is empty");
  }
  await _triggerBlobDownload(blob, finalName, contentType);
}

export async function downloadFilePost(pathWithApi, body, filename) {
  const token = localStorage.getItem("access_token");
  const res = await fetch(`${API}${pathWithApi}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try {
      const t = await res.text();
      if (t) msg = t.length > 300 ? t.slice(0, 300) : t;
    } catch (_) { /* ignore */ }
    throw new Error(msg);
  }
  const contentType = res.headers.get("content-type") || "application/octet-stream";
  const blob = await res.blob();
  if (!blob || blob.size === 0) {
    throw new Error("Downloaded file is empty");
  }
  await _triggerBlobDownload(blob, filename, contentType);
}
